import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { enqueuedPost } from "@/lib/offlineQueue";
import { Play, CheckCircle2, QrCode, ArrowLeft, ArrowRight, Users, MapPin, Check, UserX, Clock, AlertTriangle, X, Camera, Satellite } from "lucide-react";
import { toast } from "sonner";
import LeafletMap from "@/components/LeafletMap";
import CameraQrScanner from "@/components/CameraQrScanner";
import useDriverGps from "@/hooks/useDriverGps";

export default function DriverMissionDetail() {
  const { id } = useParams();
  const [mission, setMission] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [scanCode, setScanCode] = useState("");
  const [scanMode, setScanMode] = useState("boarding");
  const [events, setEvents] = useState([]);
  const [lastScan, setLastScan] = useState(null);
  const [incidentForm, setIncidentForm] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const gps = useDriverGps({ enabled: mission?.status === "active", missionId: id });

  const load = useCallback(async () => {
    const [{ data: m }, { data: ev }] = await Promise.all([
      api.get(`/missions/${id}`),
      api.get(`/missions/${id}/events`),
    ]);
    setMission(m); setEvents(ev);
  }, [id]);

  const loadTracking = useCallback(async () => {
    try { const { data } = await api.get(`/missions/${id}/tracking`); setTracking(data); }
    catch { /* ignore */ }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadTracking();
    const t = setInterval(loadTracking, 3000);
    return () => clearInterval(t);
  }, [loadTracking]);

  if (!mission) return <div className="p-6 text-sm text-zinc-500">Chargement...</div>;

  const start = async () => { try { await api.post(`/missions/${id}/start`); toast.success("Mission démarrée"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } };
  const complete = async () => { try { await api.post(`/missions/${id}/complete`); toast.success("Mission terminée"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } };
  const nextStop = async () => { try { await api.post(`/missions/${id}/next-stop`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } };

  const doScan = async (code) => {
    try {
      const res = await enqueuedPost("/scan", { mission_id: id, qr_code: code, event_type: scanMode, stop_id: mission.route?.stops?.[mission.current_stop_index]?.id });
      if (res?.queued) {
        toast.success("Scan mis en file — synchronisé dès la connexion");
        setScanCode("");
        return;
      }
      setLastScan(res.data.child);
      toast.success(`${res.data.child.first_name} ${res.data.child.last_name} — ${scanMode === "boarding" ? "Monté" : "Descendu"} ✓`);
      setScanCode("");
      load();
      setTimeout(() => setLastScan(null), 2500);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onScanSubmit = (e) => { e.preventDefault(); if (scanCode.trim()) doScan(scanCode.trim()); };

  const submitIncident = async (e) => {
    e.preventDefault();
    try {
      await api.post("/incidents", {
        mission_id: id,
        child_id: incidentForm.child_id || null,
        type: incidentForm.type,
        severity: incidentForm.severity || "low",
        message: incidentForm.message || (incidentForm.type === "absence" ? "Enfant absent" : incidentForm.type === "delay" ? `Retard ${incidentForm.delay_minutes || 0} min` : "Incident"),
        delay_minutes: parseInt(incidentForm.delay_minutes || 0, 10),
      });
      toast.success("Signalement envoyé au dispatch");
      setIncidentForm(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const currentStop = mission.route?.stops?.[mission.current_stop_index];
  const waypoints = mission.route
    ? [{ lat: mission.route.school_lat, lng: mission.route.school_lng }, ...mission.route.stops.map((s) => ({ lat: s.lat, lng: s.lng }))]
    : [];

  const childrenAtCurrent = (currentStop?.children_ids || []).map((cid) => mission.children.find((c) => c.id === cid)).filter(Boolean);

  return (
    <div className="pb-4" data-testid="driver-mission-detail">
      <div className="px-4 sm:px-6 pt-4 pb-2 flex items-center justify-between">
        <Link to="/driver" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900">
          <ArrowLeft size={14} /> Missions
        </Link>
        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${mission.status === "active" ? "bg-[#FFC60A]" : mission.status === "completed" ? "bg-zinc-900 text-white" : "bg-white"}`}>
          {mission.status}
        </span>
      </div>

      <div className="px-4 sm:px-6">
        <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tighter">{mission.route?.name}</h1>
        <div className="text-xs text-zinc-500 font-mono mt-0.5">{mission.vehicle?.plate} · capacité {mission.vehicle?.capacity}</div>
      </div>

      <div className="mt-4 px-4 sm:px-6">
        <LeafletMap waypoints={waypoints} position={tracking?.position} height={240} testId="driver-mission-map" />
        {tracking && mission.status === "active" && (
          <div className="mt-2 grid grid-cols-4 gap-0 border border-zinc-900" data-testid="driver-tracking-bar">
            <div className="p-2 text-center border-r border-zinc-900">
              <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Vitesse</div>
              <div className="font-mono font-bold text-sm">{tracking.speed_kmh} km/h</div>
            </div>
            <div className="p-2 text-center border-r border-zinc-900">
              <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">ETA</div>
              <div className="font-mono font-bold text-sm">{tracking.eta_minutes} min</div>
            </div>
            <div className={`p-2 text-center border-r border-zinc-900 ${tracking.delay_minutes > 0 ? "bg-red-500 text-white" : ""}`}>
              <div className="text-[10px] font-mono tracking-widest uppercase opacity-80">Retard</div>
              <div className="font-mono font-bold text-sm">{tracking.delay_minutes} min</div>
            </div>
            <div className={`p-2 text-center ${tracking.source === "gps" ? "bg-[#FFC60A]" : "bg-zinc-100"}`} data-testid="tracking-source">
              <div className="text-[10px] font-mono tracking-widest uppercase">Source</div>
              <div className="font-mono font-bold text-sm inline-flex items-center gap-1">
                <Satellite size={12} /> {tracking.source === "gps" ? "GPS" : "Simulé"}
              </div>
            </div>
          </div>
        )}
        {gps.error && mission.status === "active" && (
          <div className="mt-2 border border-red-500 bg-red-50 text-red-700 text-xs p-2 font-mono" data-testid="gps-error">
            GPS : {gps.error} — vérifiez les permissions de géolocalisation.
          </div>
        )}
      </div>

      {mission.status === "pending" && (
        <div className="p-4 sm:p-6">
          <button data-testid="start-mission-btn" onClick={start} className="w-full bg-[#FFC60A] border border-zinc-900 h-16 font-display font-black text-xl tracking-tighter inline-flex items-center justify-center gap-2 hover:bg-[#ffd43a]">
            <Play size={22} /> DÉMARRER LA MISSION
          </button>
        </div>
      )}

      {mission.status === "active" && (
        <>
          <div className="px-4 sm:px-6 mt-4">
            <div className="border border-zinc-900 p-4 bg-[#fafaf7]">
              <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Arrêt courant · {mission.current_stop_index + 1}/{mission.route.stops.length}</div>
              {currentStop ? (
                <>
                  <div className="font-display font-bold text-xl mt-1 flex items-center gap-2"><MapPin size={18} /> {currentStop.address}</div>
                  <div className="text-xs text-zinc-500 font-mono">{currentStop.lat.toFixed(4)}, {currentStop.lng.toFixed(4)}</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {childrenAtCurrent.map((c) => (
                      <div key={c.id} className="border border-zinc-900 px-3 py-1 text-xs inline-flex items-center gap-2">
                        <Users size={12} /> {c.first_name} {c.last_name}
                      </div>
                    ))}
                    {childrenAtCurrent.length === 0 && <span className="text-xs text-zinc-500">Aucun enfant enregistré à cet arrêt.</span>}
                  </div>
                </>
              ) : (
                <div className="text-sm text-zinc-500 mt-2">Tous les arrêts effectués.</div>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button data-testid="next-stop-btn" onClick={nextStop} className="border border-zinc-900 py-2.5 text-sm font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white inline-flex items-center justify-center gap-2">
                  Arrêt suivant <ArrowRight size={14} />
                </button>
                <button data-testid="complete-mission-btn" onClick={complete} className="bg-[#FFC60A] border border-zinc-900 py-2.5 text-sm font-bold uppercase tracking-widest hover:bg-[#ffd43a] inline-flex items-center justify-center gap-2">
                  <CheckCircle2 size={14} /> Terminer
                </button>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 mt-4">
            <div className="border border-zinc-900 p-3 bg-zinc-900 text-white">
              <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-400">Signaler</div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <button data-testid="report-absence-btn" onClick={() => setIncidentForm({ type: "absence", message: "" })}
                  className="border border-white py-2 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-zinc-900 inline-flex items-center justify-center gap-1">
                  <UserX size={14} /> Absence
                </button>
                <button data-testid="report-delay-btn" onClick={() => setIncidentForm({ type: "delay", message: "", delay_minutes: 5 })}
                  className="border border-white py-2 text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-zinc-900 inline-flex items-center justify-center gap-1">
                  <Clock size={14} /> Retard
                </button>
                <button data-testid="report-incident-btn" onClick={() => setIncidentForm({ type: "incident", message: "", severity: "medium" })}
                  className="bg-[#FFC60A] text-zinc-900 border border-[#FFC60A] py-2 text-xs font-bold uppercase tracking-widest hover:opacity-90 inline-flex items-center justify-center gap-1">
                  <AlertTriangle size={14} /> Incident
                </button>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 mt-4">
            <div className="border border-zinc-900 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Scanner QR</div>
                <div className="flex gap-0 border border-zinc-900">
                  <button data-testid="scan-mode-boarding" onClick={() => setScanMode("boarding")} className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${scanMode === "boarding" ? "bg-[#FFC60A]" : "bg-white"}`}>Montée</button>
                  <button data-testid="scan-mode-dropoff" onClick={() => setScanMode("dropoff")} className={`px-3 py-1 text-[10px] font-bold uppercase tracking-widest border-l border-zinc-900 ${scanMode === "dropoff" ? "bg-[#FFC60A]" : "bg-white"}`}>Descente</button>
                </div>
              </div>
              <form onSubmit={onScanSubmit} className="mt-3 flex gap-2">
                <input data-testid="scan-code-input" value={scanCode} onChange={(e) => setScanCode(e.target.value)}
                  placeholder="SHT-XXXXXXXXXX"
                  className="flex-1 border border-zinc-900 px-3 py-2.5 font-mono tracking-[0.15em] uppercase focus:ring-2 focus:ring-zinc-900 focus:outline-none" />
                <button data-testid="scan-submit-btn" type="submit" className="bg-zinc-900 text-white px-4 py-2.5 font-bold inline-flex items-center gap-2 hover:bg-[#FFC60A] hover:text-zinc-900 border border-zinc-900">
                  <QrCode size={16} /> Valider
                </button>
                <button type="button" data-testid="camera-scan-btn" onClick={() => setCameraOpen(true)} className="bg-[#FFC60A] border border-zinc-900 px-3 py-2.5 font-bold inline-flex items-center gap-1 hover:bg-[#ffd43a]" title="Scanner via caméra">
                  <Camera size={16} />
                </button>
              </form>
              <div className="mt-3 text-[11px] font-mono text-zinc-500">Astuce: demandez aux enfants de présenter leur badge QR.</div>

              <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-1">
                {mission.children.map((c) => (
                  <button key={c.id} data-testid={`quick-scan-${c.id}`} onClick={() => doScan(c.qr_code)}
                    className="border border-zinc-900 p-2 text-[10px] font-mono uppercase tracking-widest hover:bg-[#FFC60A]">
                    {c.first_name}
                  </button>
                ))}
              </div>

              {lastScan && (
                <div data-testid="last-scan-confirmation" className="mt-3 border-2 border-[#FFC60A] bg-white p-3 pop-in inline-flex items-center gap-2 font-bold">
                  <Check size={16} className="text-zinc-900 bg-[#FFC60A] p-0.5" /> {lastScan.first_name} {lastScan.last_name}
                </div>
              )}
            </div>
          </div>

          <div className="px-4 sm:px-6 mt-4">
            <div className="border border-zinc-900">
              <div className="px-3 py-2 bg-zinc-900 text-white text-[10px] font-mono tracking-widest uppercase">Journal des scans</div>
              <div className="divide-y divide-zinc-200 max-h-60 overflow-y-auto" data-testid="scan-log">
                {events.length === 0 ? (
                  <div className="p-3 text-xs text-zinc-500">Aucun scan pour l'instant.</div>
                ) : events.map((ev) => {
                  const c = mission.children.find((x) => x.id === ev.child_id);
                  return (
                    <div key={ev.id} className="p-2 text-xs flex items-center justify-between">
                      <span>{c?.first_name || "?"} {c?.last_name || ""} <span className="text-zinc-500 font-mono">· {ev.event_type}</span></span>
                      <span className="font-mono text-zinc-500">{ev.scanned_at.slice(11, 16)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {mission.status === "completed" && (
        <div className="p-4 sm:p-6">
          <div className="border border-zinc-900 p-6 bg-zinc-900 text-white text-center">
            <CheckCircle2 size={32} className="mx-auto text-[#FFC60A]" />
            <div className="font-display font-black text-xl tracking-tighter mt-2">Mission terminée</div>
            <div className="text-xs text-zinc-400 mt-1 font-mono">{mission.completed_at}</div>
          </div>
        </div>
      )}

      <CameraQrScanner
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onScan={(text) => {
          setCameraOpen(false);
          if (text) doScan(text.trim());
        }}
      />

      {incidentForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="incident-modal">
          <form onSubmit={submitIncident} className="bg-white border border-zinc-900 w-full max-w-md">
            <div className="flex items-center justify-between border-b border-zinc-900 p-4">
              <div className="font-display font-bold text-xl flex items-center gap-2">
                {incidentForm.type === "absence" ? <UserX size={18} /> : incidentForm.type === "delay" ? <Clock size={18} /> : <AlertTriangle size={18} />}
                {incidentForm.type === "absence" ? "Signaler une absence" : incidentForm.type === "delay" ? "Signaler un retard" : "Déclarer un incident"}
              </div>
              <button type="button" onClick={() => setIncidentForm(null)}><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              {incidentForm.type === "absence" && (
                <div>
                  <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Enfant absent</label>
                  <select data-testid="incident-child-select" required value={incidentForm.child_id || ""} onChange={(e) => setIncidentForm({ ...incidentForm, child_id: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2 bg-white">
                    <option value="">— Sélectionner —</option>
                    {mission.children.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                  </select>
                </div>
              )}
              {incidentForm.type === "delay" && (
                <div>
                  <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Retard estimé (min)</label>
                  <input data-testid="incident-delay-input" type="number" min={1} value={incidentForm.delay_minutes || 5} onChange={(e) => setIncidentForm({ ...incidentForm, delay_minutes: e.target.value })} className="mt-1 w-full border border-zinc-900 px-3 py-2" />
                </div>
              )}
              {incidentForm.type === "incident" && (
                <div>
                  <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Gravité</label>
                  <div className="mt-1 grid grid-cols-3 border border-zinc-900">
                    {["low", "medium", "high"].map((s) => (
                      <button key={s} type="button" onClick={() => setIncidentForm({ ...incidentForm, severity: s })}
                        className={`py-2 text-xs font-bold uppercase tracking-widest border-r border-zinc-900 last:border-r-0 ${incidentForm.severity === s ? (s === "high" ? "bg-red-500 text-white" : s === "medium" ? "bg-[#FFC60A]" : "bg-zinc-200") : "bg-white"}`}>
                        {s === "low" ? "Faible" : s === "medium" ? "Moyen" : "Élevé"}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Message</label>
                <textarea data-testid="incident-message-input" required rows={3} value={incidentForm.message} onChange={(e) => setIncidentForm({ ...incidentForm, message: e.target.value })} placeholder="Décrivez la situation..." className="mt-1 w-full border border-zinc-900 px-3 py-2 resize-none" />
              </div>
            </div>
            <div className="border-t border-zinc-900 p-4 flex justify-end gap-2">
              <button type="button" onClick={() => setIncidentForm(null)} className="px-4 py-2 border border-zinc-900 font-semibold">Annuler</button>
              <button data-testid="submit-incident-btn" type="submit" className="px-4 py-2 bg-[#FFC60A] border border-zinc-900 font-bold">Envoyer au dispatch</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
